import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, ChevronLeft, ChevronsDown, ChevronsUp, Download, Edit2 } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/shadcn/accordion.tsx';
import { Badge } from '@/components/shadcn/badge.tsx';
import { Button } from '@/components/shadcn/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card.tsx';
import { Checkbox } from '@/components/shadcn/checkbox.tsx';
import { Input } from '@/components/shadcn/input.tsx';
import { Label } from '@/components/shadcn/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table.tsx';
import { govtIntakeOrganizationType, govtModulePricingBreakdown } from '@/data/govtIntakeStaticData';
import { getGovernmentPricingBreakdown, postGovernmentPricingBreakdown } from '../../api/pricing';
import { getGovernmentIntake } from '../../api/queries';
import type { GovernmentPricingModule, Root } from '../../api/types';
import type {
  AdminDetailData,
  AdminDetailFile,
  AdminDetailSubmission,
  AdminDetailSubmissionStatus,
  AdminDetailSystem,
  GetByIdResponseData,
  NeedsRollupData,
} from '../../types/common.types';

// Summary shape of the discovery file row selected in AdminListView.
interface DiscoveryFileSummary {
  id: string;
  apiId: number | string;
  fileName: string;
  jurisdiction: string;
  cityOrAgencyName?: string;
  state?: string;
  submissionCount: number;
  departments: string[];
  objectFocus: string[];
  reportingNeeded: string;
  formsNeeded: string;
  legacyDataIngestion: string;
  lastUpdated: string;
  status: string;
}

/** List item shape from list API — passed from AdminListView to avoid a second API call. Accepts list API item shape (id, accountId, payload, etc.). */
type ListItemForDetail = Record<string, unknown> & { payload?: Root; accountId?: number | null; id?: number };

// Transform API response to AdminDetailData
function transformApiResponseToDetailData(intakeDetail: Root | null, responseData?: GetByIdResponseData | null): AdminDetailData {
  if (!intakeDetail) {
    return { annualBudget: 0, submissions: [] };
  }

  const submissions: AdminDetailSubmission[] = [];
  const primaryObjects = intakeDetail.primaryObjectOfWork || [];
  const entryContact = intakeDetail.entryContact;
  const payloadContact = responseData?.payload?.entryContact ?? entryContact;

  // Process each primary object of work (programs, projects, properties)
  primaryObjects.forEach((obj, index) => {
    const department = responseData?.department ?? entryContact?.department ?? '';
    const submittedBy = payloadContact?.fullName ?? responseData?.fullName ?? '';
    const email = responseData?.submittedByEmail ?? payloadContact?.email ?? '';
    const phone = payloadContact?.phone ?? '';
    const submittedDate = new Date().toISOString().slice(0, 10);

    const role = responseData?.payload?.roleContext?.roleDescription ?? '';
    const dataFrequency = '';
    // Set primaryRole from payload.entryContact.jobTitle
    const primaryRole = payloadContact?.jobTitle ?? responseData?.jobTitle ?? '';
    const problemToSolve = responseData?.payload?.roleContext?.fromSeinTerminalPerspective ?? '';
    let reportingNeeded = '';
    let formsNeeded = '';
    let formsCount: number | undefined;
    let legacyData = '';
    const systems: AdminDetailSystem[] = [];
    const painPoints: string[] = [];
    const files: AdminDetailFile[] = [];

    // Process Programs
    if (obj.programs) {
      const scoping = obj.programs.scoping;
      formsNeeded = scoping?.programForms || '';
      formsCount = scoping?.formCount ? parseInt(scoping.formCount, 10) : undefined;
      reportingNeeded = scoping?.automatedReporting || '';
      legacyData = scoping.consolidate || '';

      for (const f of scoping?.automatedReportingFile ?? []) {
        files.push({ ...f, label: 'Reporting' });
      }
      for (const f of scoping?.automatedFormFile ?? []) {
        files.push({ ...f, label: 'Forms' });
      }

      // Extract pain points
      if (obj.programs.painPoints) {
        const pp = obj.programs.painPoints;
        if (pp.painPoint1) painPoints.push(pp.painPoint1);
        if (pp.painPoint2) painPoints.push(pp.painPoint2);
        if (pp.painPoint3) painPoints.push(pp.painPoint3);
        if (pp.biggestBurden) painPoints.push(pp.biggestBurden);
        if (pp.delaysAndErrors) painPoints.push(pp.delaysAndErrors);
      }

      // Extract systems
      if (obj.programs.currentSystems?.systems) {
        const systemsList = obj.programs.currentSystems.systems;

        for (const sys of systemsList) {
          systems.push({
            name: sys.name,
            purpose: sys.notes,
            access: sys.vendor,
          });
        }
      }
    }

    // Process Projects
    if (obj.projects) {
      const scoping = obj.projects.scoping;
      formsNeeded = scoping?.programForms || formsNeeded || '';
      formsCount = scoping?.formCount ? parseInt(scoping.formCount, 10) : formsCount;
      reportingNeeded = scoping?.automatedReporting || reportingNeeded || '';
      legacyData = scoping.consolidate || '';

      for (const f of scoping?.automatedReportingFile ?? []) {
        files.push({ ...f, label: 'Reporting' });
      }
      for (const f of scoping?.automatedFormFile ?? []) {
        files.push({ ...f, label: 'Forms' });
      }

      // Extract systems
      if (obj.projects.currentSystems?.systems) {
        const projectsSystems = obj.projects.currentSystems.systems;
        for (const sys of projectsSystems) {
          systems.push({
            name: sys.name,
            purpose: sys.notes || '',
            access: sys.vendor || '',
          });
        }
      }
    }

    // Process Properties
    if (obj.properties) {
      const needs = obj.properties.needs;
      formsNeeded = needs?.programForms || formsNeeded || '';
      formsCount = needs?.formCount ? parseInt(needs.formCount, 10) : formsCount;
      reportingNeeded = needs?.automatedReporting || reportingNeeded || '';
      legacyData = needs.consolidate || '';

      for (const f of needs?.automatedReportingFile ?? []) {
        files.push({ ...f, label: 'Reporting' });
      }
      for (const f of needs?.automatedFormFile ?? []) {
        files.push({ ...f, label: 'Forms' });
      }

      // Extract pain points
      if (obj.properties.painPoints) {
        const pp = obj.properties.painPoints;
        if (pp.painPoint1) painPoints.push(pp.painPoint1);
        if (pp.painPoint2) painPoints.push(pp.painPoint2);
        if (pp.painPoint3) painPoints.push(pp.painPoint3);
        if (pp.leadershipAsksFor) painPoints.push(`Leadership asks for: ${pp.leadershipAsksFor}`);
      }

      // Extract systems
      if (obj.properties.operationalSystems?.systems) {
        const propertiesSystem = obj.properties.operationalSystems.systems;
        for (const sys of propertiesSystem) {
          systems.push({
            name: sys.name,
            purpose: sys.notes || '',
            access: sys.vendor || '',
          });
        }
      }
    }

    // Add current systems from root level
    if (intakeDetail.currentSystems?.systems) {
      const currentSystemsList = intakeDetail.currentSystems.systems;
      for (const sys of currentSystemsList) {
        systems.push({
          name: sys.name,
          purpose: sys.note || '',
          access: sys.vendor || '',
        });
      }
    }

    // Set default values if nothing was found
    const finalPrimaryRole = primaryRole || 'Programs / Projects / Properties';
    if (painPoints.length === 0) {
      painPoints.push('No specific pain points identified');
    }
    if (systems.length === 0) {
      systems.push({
        name: '',
        purpose: '',
        access: '',
      });
    }

    submissions.push({
      id: `${index + 1}`,
      department,
      status: 'submitted' as const,
      submittedBy,
      email,
      phone,
      submittedDate,
      data: {
        role,
        dataFrequency,
        primaryRole: finalPrimaryRole,
        problemToSolve: problemToSolve,
        reportingNeeded,
        formsNeeded,
        formsCount,
        legacyData,
        systems,
        painPoints,
        files,
      },
    });
  });

  // If no submissions were created, create a default one
  if (submissions.length === 0) {
    const department = responseData?.department ?? intakeDetail.entryContact?.department ?? '';
    const submittedBy = payloadContact?.fullName ?? responseData?.fullName ?? '';
    const email = responseData?.submittedByEmail ?? payloadContact?.email ?? '';
    const phone = payloadContact?.phone ?? '';
    const primaryRole = payloadContact?.jobTitle ?? responseData?.jobTitle ?? 'Programs / Projects / Properties';

    submissions.push({
      id: '1',
      department,
      status: 'submitted' as const,
      submittedBy,
      email,
      phone,
      submittedDate: new Date().toISOString().slice(0, 10),
      data: {
        role: intakeDetail.roleContext?.roleDescription,
        dataFrequency: '',
        primaryRole: primaryRole,
        problemToSolve: '',
        reportingNeeded: '',
        formsNeeded: '',
        legacyData: '',
        systems:
          intakeDetail.currentSystems?.systems?.map((sys) => ({
            name: sys.name,
            purpose: sys.note || '',
            access: sys.vendor || '',
          })) || [],
        painPoints: ['No specific pain points identified'],
        files: [],
      },
    });
  }

  // Use a default annual budget (can be enhanced later)
  const annualBudget = govtModulePricingBreakdown[0].total;

  return { annualBudget, submissions };
}

interface AdminDetailViewProps {
  fileId: string;
  apiId: string | number;
  file: DiscoveryFileSummary;
  /** Raw list from list API — used instead of calling getGovernmentIntakesByIds. */
  listItems?: ListItemForDetail[] | unknown[];
  onBack: () => void;
}

export function AdminDetailView({ apiId, file, listItems = [], onBack }: AdminDetailViewProps) {
  const intakeId = String(apiId);
  const detailQuery = useQuery({
    queryKey: ['government-intake', 'admin-detail', intakeId],
    queryFn: () => getGovernmentIntake({ intakeId }),
    enabled: intakeId.length > 0,
  });
  const pricingQuery = useQuery({
    queryKey: ['government-intake', 'admin-pricing', intakeId],
    queryFn: () => getGovernmentPricingBreakdown({ intakeId }),
    enabled: intakeId.length > 0,
  });
  const savePricingMutation = useMutation({
    mutationFn: (pricingBreakdown: GovernmentPricingModule[]) => postGovernmentPricingBreakdown({ intakeId, pricingBreakdown }),
  });

  // Detail data derived from listItems (no API call when listItems is provided)
  const [intakeDetail, setIntakeDetail] = useState<Root | null>(null);
  const [apiResponseData, setApiResponseData] = useState<GetByIdResponseData | null>(null);
  /** All matching items for this accountId — used to merge submissions so departmentList includes every department. */
  const [matchingListItems, setMatchingListItems] = useState<ListItemForDetail[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState(true);
  const [detailError, setDetailError] = useState<string | null>(null);

  const id = useId();

  // Prefer GET /:id from backend; fallback to listItems when detail query is unavailable.
  useEffect(() => {
    if (detailQuery.data) {
      const responseData = detailQuery.data.data;
      const payload = responseData?.payload ?? (responseData as unknown as Root) ?? null;
      setDetailError(null);
      setMatchingListItems([]);
      setIntakeDetail(payload);
      setApiResponseData(responseData ?? null);
      setIsLoadingDetail(false);
      return;
    }

    if (detailQuery.isLoading) {
      setIsLoadingDetail(true);
      return;
    }

    setDetailError(null);
    const items = (listItems ?? []) as ListItemForDetail[];
    if (items.length === 0) {
      setMatchingListItems([]);
      setIntakeDetail(null);
      setApiResponseData(null);
      if (detailQuery.error) {
        console.error('Failed to fetch government intake detail.', detailQuery.error);
        setDetailError('Unable to load intake details. Please try again.');
      }
      setIsLoadingDetail(false);
      return;
    }
    setMatchingListItems(items);
    const first = items[0];
    const payload = first.payload ?? (first as unknown as Root);
    setApiResponseData(first as unknown as GetByIdResponseData);
    setIntakeDetail(payload ?? null);
    setIsLoadingDetail(false);
  }, [detailQuery.data, detailQuery.error, detailQuery.isLoading, listItems]);

  // Transform API response to component data structure — merge all matching items so departmentList has every
  // department
  const fileData = (() => {
    if (matchingListItems.length === 0) {
      return transformApiResponseToDetailData(intakeDetail, apiResponseData);
    }
    const allSubmissions: AdminDetailSubmission[] = [];
    let annualBudget = 0;
    for (const [itemIndex, item] of matchingListItems.entries()) {
      const payload = item.payload ?? (item as unknown as Root);
      const data = transformApiResponseToDetailData(payload, item as unknown as GetByIdResponseData);
      annualBudget = data.annualBudget;
      for (const s of data.submissions) {
        allSubmissions.push({ ...s, id: `${itemIndex + 1}-${s.id}` });
      }
    }
    return { annualBudget, submissions: allSubmissions };
  })();

  // Derive Departments count for Needs Rollup from all matching payloads:
  // count how many times "Software systems" appears in programs.scoping.dataLocations
  const departmentsFromSoftwareSystems = useMemo(() => {
    const items = (matchingListItems.length > 0 ? matchingListItems : (listItems ?? [])) as ListItemForDetail[];
    let count = 0;

    for (const item of items) {
      const payload = (item.payload ?? (item as unknown as Root)) as Root;
      if (!payload?.primaryObjectOfWork) continue;

      for (const obj of payload.primaryObjectOfWork) {
        const locations = obj.programs?.scoping?.dataLocations ?? [];
        for (const loc of locations) {
          if (typeof loc === 'string' && loc.toLowerCase() === 'software systems') {
            count += 1;
          }
        }
      }
    }

    return count.toString();
  }, [matchingListItems, listItems]);

  // Derive Staff Users count for Needs Rollup from all matching payloads:
  // sum approxParticipants under programs.scoping.approxParticipants (when numeric)
  const staffUsersFromApproxParticipants = useMemo(() => {
    const items = (matchingListItems.length > 0 ? matchingListItems : (listItems ?? [])) as ListItemForDetail[];
    let total = 0;

    for (const item of items) {
      const payload = (item.payload ?? (item as unknown as Root)) as Root;
      if (!payload?.primaryObjectOfWork) continue;

      for (const obj of payload.primaryObjectOfWork) {
        const approx = obj.programs?.scoping?.approxParticipants as unknown;
        if (typeof approx === 'number') {
          total += approx;
        } else if (typeof approx === 'string') {
          const parsed = parseInt(approx.replace(/[^0-9]/g, ''), 10);
          if (!Number.isNaN(parsed)) {
            total += parsed;
          }
        }
      }
    }

    return total.toString();
  }, [matchingListItems, listItems]);

  // Derive organization size label from current Staff Users count using govtIntakeOrganizationType ranges
  const organizationSizeLabel = useMemo(() => {
    const numeric = parseInt(String(staffUsersFromApproxParticipants ?? '').replace(/[^0-9]/g, ''), 10);
    if (Number.isNaN(numeric) || numeric <= 0) {
      return 'Unknown';
    }

    const bucket = govtIntakeOrganizationType.find(({ minStaff, maxStaff }) => {
      if (minStaff != null && numeric < minStaff) return false;
      if (maxStaff != null && numeric > maxStaff) return false;
      return true;
    });

    if (!bucket) {
      return 'Unknown';
    }

    const range = bucket.maxStaff == null ? `${bucket.minStaff}+ users` : `${bucket.minStaff}–${bucket.maxStaff} users`;

    return `${bucket.label} (${range})`;
  }, [staffUsersFromApproxParticipants]);

  // Derive Projects (number of properties) for Needs Rollup from all matching payloads:
  // show numberOfProperties from payload.primaryObjectOfWork[].projects.scoping.numberOfProperties if present; otherwise 0
  const projectsFromNumberOfProperties = useMemo(() => {
    const items = (matchingListItems.length > 0 ? matchingListItems : (listItems ?? [])) as ListItemForDetail[];

    for (const item of items) {
      const payload = (item.payload ?? (item as unknown as Root)) as Root;
      if (!payload?.primaryObjectOfWork) continue;

      for (const obj of payload.primaryObjectOfWork) {
        const value = obj.projects?.scoping?.numberOfProperties as unknown;
        if (typeof value === 'number') {
          return value.toString();
        }
        if (typeof value === 'string' && value.trim().length > 0) {
          return value;
        }
      }
    }

    return '0';
  }, [matchingListItems, listItems]);

  // Sum anticipatedPrograms across the list passed from AdminListView (that row's items only)
  const programsFromAnticipatedPrograms = useMemo(() => {
    const items = (matchingListItems.length > 0 ? matchingListItems : (listItems ?? [])) as ListItemForDetail[];
    let total = 0;

    for (const item of items) {
      const payload = (item.payload ?? (item as unknown as Root)) as Root;
      if (!payload?.primaryObjectOfWork) continue;

      for (const obj of payload.primaryObjectOfWork) {
        const value = (obj.programs?.scoping as Record<string, unknown> | undefined)?.anticipatedPrograms;
        if (typeof value === 'number' && !Number.isNaN(value)) {
          total += value;
        } else if (typeof value === 'string' && value.trim().length > 0) {
          const parsed = parseInt(value.replace(/[^0-9]/g, ''), 10);
          if (!Number.isNaN(parsed)) {
            total += parsed;
          }
        }
      }
    }

    return total > 0 ? total.toString() : '0';
  }, [matchingListItems, listItems]);

  // Extract needs rollup data from API response
  const extractNeedsRollupData = (detail: Root | null, responseData: GetByIdResponseData | null): NeedsRollupData => {
    const empty: NeedsRollupData = {
      publicForms: '0',
      programs: '0',
      projects: '0',
      submitters: '0',
      staffUsers: '0',
      departments: '0',
    };
    if (!detail) {
      return empty;
    }

    const primaryObjects = detail.primaryObjectOfWork || [];
    let totalForms = 0;
    let programsCount = 0;
    let projectsCount = 0;

    for (const obj of primaryObjects) {
      if (obj.programs) {
        programsCount++;
        const formCount = obj.programs.scoping?.formCount;
        if (formCount) {
          totalForms += parseInt(formCount, 10) || 0;
        }
      }
      if (obj.projects) {
        projectsCount++;
        const formCount = obj.projects.scoping?.formCount;
        if (formCount) {
          totalForms += parseInt(formCount, 10) || 0;
        }
      }
      if (obj.properties) {
        const formCount = obj.properties.needs?.formCount;
        if (formCount) {
          totalForms += parseInt(formCount, 10) || 0;
        }
      }
    }

    const submittersCount = responseData?.department ? '0' : '0';
    const departmentsCount = responseData?.department ? '0' : '0';

    return {
      publicForms: totalForms > 0 ? totalForms.toString() : '0',
      programs: programsCount > 0 ? programsCount.toString() : '0',
      projects: projectsCount > 0 ? projectsCount.toString() : '0',
      submitters: submittersCount,
      staffUsers: '0',
      departments: departmentsCount,
    };
  };

  const needsRollupData = extractNeedsRollupData(intakeDetail, apiResponseData);

  // Editable Needs Rollup state - initialized from API response
  const [publicForms, setPublicForms] = useState(needsRollupData.publicForms);
  const [programs, setPrograms] = useState(programsFromAnticipatedPrograms);
  const [projects, setProjects] = useState(projectsFromNumberOfProperties);
  const [submitters, setSubmitters] = useState(needsRollupData.submitters);
  const [staffUsers, setStaffUsers] = useState('0');
  const [departments, setDepartments] = useState(departmentsFromSoftwareSystems);

  // Update needs rollup when API data loads (staffUsers defaults to 0; user can increase)
  useEffect(() => {
    const data = extractNeedsRollupData(intakeDetail, apiResponseData);
    setPublicForms(data.publicForms);
    setPrograms(programsFromAnticipatedPrograms);
    setProjects(projectsFromNumberOfProperties);
    setSubmitters(data.submitters);
    setDepartments(departmentsFromSoftwareSystems);
  }, [intakeDetail, apiResponseData, programsFromAnticipatedPrograms, projectsFromNumberOfProperties, departmentsFromSoftwareSystems]);

  // Get unique department list for filters
  const departmentList = [...new Set(fileData.submissions.map((s) => s.department))];
  type DepartmentType = (typeof departmentList)[number];

  // Pain points filter
  const [painPointFilter, setPainPointFilter] = useState<'all' | DepartmentType>('all');

  // Accordion expand/collapse state
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Needs and Pricing state
  const [needsConfirmed, setNeedsConfirmed] = useState(false);
  const [pricingApproved, setPricingApproved] = useState(false);

  useEffect(() => {
    const existingPricing = pricingQuery.data?.data?.pricingBreakdown;
    if (existingPricing && existingPricing.length > 0) {
      // Mark pricing as previously approved but keep needsConfirmed false so
      // the user can still edit the Needs section dropdowns and checkboxes.
      setPricingApproved(true);
    }
  }, [pricingQuery.data]);

  // Platform state
  const [platformAccepted, setPlatformAccepted] = useState(false);

  // Open Questions / Risks reviewed state
  const [questionsReviewed, setQuestionsReviewed] = useState(false);

  // Sherpa state
  const [sherpaAccepted, setSherpaAccepted] = useState(false);
  const [sherpaTier, setSherpaTier] = useState('Starter');
  const [sherpaModules, setSherpaModules] = useState({
    budgeting: true,
    timeline: true,
    reporting: false,
  });

  // Rover state
  const [roverAccepted, setRoverAccepted] = useState(false);
  const [roverTier, setRoverTier] = useState('Starter');
  const [roverModules, setRoverModules] = useState({
    workflow: true,
    notifications: true,
    analytics: false,
  });

  // Atlas state
  const [atlasAccepted, setAtlasAccepted] = useState(false);
  const [atlasTier, setAtlasTier] = useState('Starter');
  const [atlasModules, setAtlasModules] = useState({
    mapping: true,
    inspection: true,
  });

  // De-duplicate pain points
  const allPainPoints = fileData.submissions.flatMap((s) => s.data.painPoints);
  const uniquePainPoints = [...new Set(allPainPoints)];
  const painPointsWithDepts = uniquePainPoints.map((point) => {
    const depts = fileData.submissions
      .filter((s) => (s.data.painPoints as readonly string[]).includes(point))
      .map((s) => s.department) as DepartmentType[];
    return { point, departments: depts };
  });

  // Filter pain points by department — when a department is clicked, only show pain points that belong to that
  // department
  const filteredPainPoints =
    painPointFilter === 'all' ? painPointsWithDepts : painPointsWithDepts.filter((item) => item.departments.includes(painPointFilter));

  // One row per department in Section 2 (Department Details)
  const uniqueSubmissions = (() => {
    const seen = new Set<string>();
    return fileData.submissions.filter((s) => {
      const key = s.department;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  const handleExpandAll = () => {
    setExpandedItems(uniqueSubmissions.map((s) => s.id));
  };

  const handleCollapseAll = () => {
    setExpandedItems([]);
  };

  const getStatusBadge = (status: AdminDetailSubmissionStatus) => {
    const variants = {
      submitted: { label: 'Submitted', className: 'bg-primary/10 text-primary border-primary/20' },
      'in-progress': { label: 'In Progress', className: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20' },
      'not-started': { label: 'Not Started', className: 'bg-muted text-muted-foreground' },
    };
    const config = variants[status];
    return (
      <Badge variant='outline' className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const sherpaPricingData = govtModulePricingBreakdown.find((module) => module.name === 'Sherpa');
  const roverPricingData = govtModulePricingBreakdown.find((module) => module.name === 'Rover');

  const platformFee = govtModulePricingBreakdown[1].total;
  const sherpaBaseFee = sherpaPricingData?.total ?? 0;
  // Calculate sherpaModuleFees dynamically based on checked modules and their costs from pricing data
  const sherpaModuleFees = useMemo(() => {
    if (!sherpaPricingData?.features) return 0;
    let total = 0;
    if (sherpaModules.budgeting) {
      const feature = sherpaPricingData.features.find((f) => f.name === 'Budget Tracking');
      if (feature) total += feature.cost;
    }
    if (sherpaModules.timeline) {
      const feature = sherpaPricingData.features.find((f) => f.name === 'Timeline Visualization');
      if (feature) total += feature.cost;
    }
    if (sherpaModules.reporting) {
      const feature = sherpaPricingData.features.find((f) => f.name === 'Advanced Reporting');
      if (feature) total += feature.cost;
    }
    return total;
  }, [sherpaModules, sherpaPricingData]);
  const roverBaseFee = roverPricingData?.total ?? 0;
  // Calculate roverModuleFees dynamically based on checked modules and their costs from pricing data
  const roverModuleFees = useMemo(() => {
    if (!roverPricingData?.features) return 0;
    let total = 0;
    if (roverModules.workflow) {
      const feature = roverPricingData.features.find((f) => f.name === 'Workflow Engine');
      if (feature) total += feature.cost;
    }
    if (roverModules.notifications) {
      const feature = roverPricingData.features.find((f) => f.name === 'Email Notifications');
      if (feature) total += feature.cost;
    }
    if (roverModules.analytics) {
      const feature = roverPricingData.features.find((f) => f.name === 'Form Analytics');
      if (feature) total += feature.cost;
    }
    return total;
  }, [roverModules, roverPricingData]);

  const atlasPricingData = govtModulePricingBreakdown.find((module) => module.name === 'Atlas');

  const atlasBaseFee = atlasPricingData?.total ?? 0;
  // Calculate atlasModuleFees dynamically based on checked modules and their costs from pricing data
  const atlasModuleFees = useMemo(() => {
    if (!atlasPricingData?.features) return 0;
    let total = 0;
    if (atlasModules.mapping) {
      const feature = atlasPricingData.features.find((f) => f.name === 'GIS Integration');
      if (feature) total += feature.cost;
    }
    if (atlasModules.inspection) {
      const feature = atlasPricingData.features.find((f) => f.name === 'Inspection Workflow');
      if (feature) total += feature.cost;
    }
    return total;
  }, [atlasModules, atlasPricingData]);

  const annualTotal = platformFee + sherpaBaseFee + sherpaModuleFees + roverBaseFee + roverModuleFees + atlasBaseFee + atlasModuleFees;
  const budgetPercentage = ((annualTotal / fileData.annualBudget) * 100).toFixed(2);

  const pricingBreakdownForSave: GovernmentPricingModule[] = [
    {
      name: 'SEIN',
      category: 'Platform',
      total: platformFee,
    },
    {
      name: 'Sherpa',
      category: 'Project Management',
      total: sherpaBaseFee + sherpaModuleFees,
    },
    {
      name: 'Rover',
      category: 'Public Forms',
      total: roverBaseFee + roverModuleFees,
    },
    {
      name: 'Atlas',
      category: 'Property Management',
      total: atlasBaseFee + atlasModuleFees,
    },
  ];

  const handleSherpaModuleChange = (key: keyof typeof sherpaModules, checked: boolean) => {
    setSherpaModules({ ...sherpaModules, [key]: checked });
  };

  const handleRoverModuleChange = (key: keyof typeof roverModules, checked: boolean) => {
    setRoverModules({ ...roverModules, [key]: checked });
  };

  const handleAtlasModuleChange = (key: keyof typeof atlasModules, checked: boolean) => {
    setAtlasModules({ ...atlasModules, [key]: checked });
  };

  const handleConfirmNeeds = () => {
    if (platformAccepted && sherpaAccepted && roverAccepted && atlasAccepted && questionsReviewed) {
      setNeedsConfirmed(true);
    }
  };

  const handleApprovePricing = async () => {
    if (!needsConfirmed || savePricingMutation.isLoading) return;

    try {
      await savePricingMutation.mutateAsync(pricingBreakdownForSave);
      setPricingApproved(true);
    } catch (error) {
      console.error('Failed to persist pricing breakdown.', error);
    }
  };

  return (
    <div className='space-y-8 pb-12'>
      {/* Header */}
      <div className='flex  items-center justify-between gap-4'>
        <Button variant='ghost' size='sm' onClick={onBack} className='gap-2'>
          <ChevronLeft className='h-4 w-4' />
          Back to Files
        </Button>

        <div className='flex flex-col items-center gap-2 mb-0'>
          <h2 className='mb-0 text-2xl font-semibold text-foreground'>{file.fileName}</h2>
          <p className='text-base text-muted-foreground'>{file.jurisdiction}</p>
        </div>

        <Button variant='outline' className='gap-2'>
          <Download className='h-4 w-4' />
          Export Summary
        </Button>
      </div>

      {/* Loading/Error States */}
      {isLoadingDetail && <div className='text-center py-8 text-muted-foreground'>Loading intake details...</div>}
      {detailError && (
        <div className='text-xs text-yellow-700 bg-yellow-100 border border-yellow-200 rounded px-2 py-1 inline-block'>{detailError}</div>
      )}

      {/* SECTION 1: INTAKE SUMMARY */}
      <div>
        <div className='mb-4'>
          <h2 className='text-xl font-semibold text-foreground mb-1'>Intake Summary</h2>
          <p className='text-sm text-muted-foreground'>Executive overview of discovery findings and anticipated needs</p>
        </div>

        {/* Two-column layout: Needs Rollup | Pain Points */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {/* LEFT COLUMN: Needs Rollup (Editable) */}
          <Card>
            <CardHeader className='pb-3'>
              <CardTitle className='text-base'>Needs Rollup</CardTitle>
              <p className='text-xs text-muted-foreground'>Editable estimates used for proposal scoping and pricing</p>
            </CardHeader>
            <CardContent>
              <div className='grid grid-cols-3 gap-3'>
                {/* Public Forms */}
                <div className='space-y-1.5'>
                  <Label htmlFor='public-forms' className='text-xs text-muted-foreground'>
                    Public Forms Needed
                  </Label>
                  <Input
                    id={`public-forms1-${id}`}
                    type='number'
                    min='0'
                    value={publicForms}
                    onChange={(e) => setPublicForms(e.target.value)}
                    className='h-9 text-center font-semibold'
                  />
                </div>

                {/* Programs */}
                <div className='space-y-1.5'>
                  <Label htmlFor={`programs-${id}`} className='text-xs text-muted-foreground'>
                    Programs Anticipated
                  </Label>
                  <Input
                    id={`programs-${id}`}
                    type='number'
                    min='0'
                    value={programs}
                    onChange={(e) => setPrograms(e.target.value)}
                    className='h-9 text-center font-semibold'
                  />
                </div>

                {/* Projects */}
                <div className='space-y-1.5'>
                  <Label htmlFor={`projects-${id}`} className='text-xs text-muted-foreground'>
                    Projects Anticipated
                  </Label>
                  <Input
                    id={`projects-${id}`}
                    type='number'
                    min='0'
                    value={projects}
                    onChange={(e) => setProjects(e.target.value)}
                    className='h-9 text-center font-semibold'
                  />
                </div>

                {/* Expected Submitters */}
                <div className='space-y-1.5'>
                  <Label htmlFor={`submitters-${id}`} className='text-xs text-muted-foreground'>
                    Expected Submitters
                  </Label>
                  <Input
                    id={`submitters-${id}`}
                    type='number'
                    min='0'
                    value={submitters}
                    onChange={(e) => setSubmitters(e.target.value)}
                    className='h-9 text-center font-semibold'
                  />
                </div>

                {/* Staff Users */}
                <div className='space-y-1.5'>
                  <Label htmlFor={`staff-users-${id}`} className='text-xs text-muted-foreground'>
                    Staff Users
                  </Label>
                  <Input
                    id={`staff-users-${id}`}
                    type='number'
                    min='0'
                    value={staffUsers}
                    onChange={(e) => setStaffUsers(e.target.value)}
                    className='h-9 text-center font-semibold'
                  />
                </div>

                {/* Departments */}
                <div className='space-y-1.5'>
                  <Label htmlFor={`departments-${id}`} className='text-xs text-muted-foreground'>
                    Departments Using Software
                  </Label>
                  <Input
                    id={`departments-${id}`}
                    type='number'
                    min='0'
                    disabled
                    value={departments}
                    onChange={(e) => setDepartments(e.target.value)}
                    className='h-9 text-center font-semibold'
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* RIGHT COLUMN: Pain Points (Deduplicated) */}
          <Card>
            <CardHeader className='pb-3'>
              <CardTitle className='text-base'>Pain Points</CardTitle>
              <p className='text-xs text-muted-foreground mb-3'>Deduplicated operational issues identified during intake</p>

              {/* Filter chips */}
              <div className='flex flex-wrap gap-2'>
                <Badge
                  variant={painPointFilter === 'all' ? 'default' : 'outline'}
                  className='cursor-pointer'
                  onClick={() => setPainPointFilter('all')}
                >
                  All
                </Badge>
                {departmentList.map((dept) => (
                  <Badge
                    key={dept}
                    variant={painPointFilter === dept ? 'default' : 'outline'}
                    className='cursor-pointer'
                    onClick={() => setPainPointFilter(dept as DepartmentType)}
                  >
                    {dept}
                  </Badge>
                ))}
              </div>
            </CardHeader>
            <CardContent className='max-h-[320px] overflow-y-auto'>
              <ul className='space-y-3'>
                {filteredPainPoints.map((item) => (
                  <li key={item.point} className='text-sm flex items-start gap-2'>
                    <span className='text-muted-foreground mt-0.5'>•</span>
                    <div className='flex-1'>
                      <span className='text-foreground'>{item.point}</span>
                      <div className='flex flex-wrap gap-1 mt-1.5'>
                        {(painPointFilter === 'all' ? item.departments : item.departments.filter((d) => d === painPointFilter)).map(
                          (dept) => (
                            <Badge key={dept} variant='outline' className='text-xs py-0 px-1.5'>
                              {dept}
                            </Badge>
                          )
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* SECTION 2: DEPARTMENT DETAILS */}
      <div>
        <div className='flex items-center justify-between mb-4'>
          <div>
            <h2 className='text-xl font-semibold text-foreground mb-1'>Department Details</h2>
            <p className='text-sm text-muted-foreground'>Supporting evidence and department-level inputs</p>
          </div>
          <div className='flex gap-2'>
            <Button variant='outline' size='sm' onClick={handleExpandAll} className='gap-2'>
              <ChevronsDown className='h-4 w-4' />
              Expand All
            </Button>
            <Button variant='outline' size='sm' onClick={handleCollapseAll} className='gap-2'>
              <ChevronsUp className='h-4 w-4' />
              Collapse All
            </Button>
          </div>
        </div>

        <Accordion type='multiple' value={expandedItems} onValueChange={setExpandedItems} className='space-y-3'>
          {uniqueSubmissions.map((submission) => (
            <AccordionItem key={submission.id} value={submission.id} className='border border-border rounded-md px-4 bg-card'>
              <AccordionTrigger className='hover:no-underline py-4'>
                <div className='flex items-center justify-between w-full pr-4'>
                  <div className='flex items-center gap-3'>
                    <span className='font-medium text-foreground'>{submission.department}</span>
                    {getStatusBadge(submission.status)}
                  </div>
                  <div className='flex items-center gap-2 text-xs'>
                    {'formsCount' in submission.data && submission.data.formsCount && (
                      <Badge variant='outline' className='bg-muted/50'>
                        {submission.data.formsCount} Forms
                      </Badge>
                    )}
                    <Badge variant='outline' className='bg-muted/50'>
                      {submission.data.systems.length} Systems
                    </Badge>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className='pt-4 pb-6 space-y-6'>
                <div>
                  <h4 className='text-sm font-medium text-foreground mb-3'>Contact Info</h4>
                  <div className='grid w-full grid-cols-4 gap-x-8 gap-y-3 text-sm'>
                    <div>
                      <span className='text-muted-foreground text-xs'>Name</span>
                      <p className='font-medium'>{submission.submittedBy}</p>
                    </div>
                    {submission.email?.trim() && (
                      <div>
                        <span className='text-muted-foreground text-xs'>Email</span>
                        <p className='font-medium'>{submission.email?.trim()}</p>
                      </div>
                    )}

                    {submission.phone?.trim() && (
                      <div>
                        <span className='text-muted-foreground text-xs'>Phone</span>
                        <p className='font-medium'>{submission.phone?.trim()}</p>
                      </div>
                    )}

                    <div>
                      <span className='text-muted-foreground text-xs'>Submitted</span>
                      <p className='font-medium'>{submission.submittedDate}</p>
                    </div>
                  </div>
                </div>

                {/* Role & Context */}
                <div className='pt-4 border-t border-border'>
                  <h4 className='text-sm font-medium text-foreground mb-3'>Role & Context</h4>
                  <div className='grid w-full grid-cols-4 gap-x-8 gap-y-3 text-sm'>
                    <div>
                      <span className='text-muted-foreground text-xs'>Role</span>
                      <p className='mt-1'>{submission.data.primaryRole}</p>
                    </div>
                    <div>
                      <span className='text-muted-foreground text-xs'>Primary Role</span>
                      <p className='font-medium'>{submission.data.role}</p>
                    </div>
                    <div>
                      <span className='text-muted-foreground text-xs'>Problem to Solve</span>
                      <p className='mt-1'>{submission.data.problemToSolve}</p>
                    </div>
                  </div>
                </div>

                {/* Current Systems */}
                <div className='pt-4 border-t border-border'>
                  <h4 className='text-sm font-medium text-foreground mb-3'>Current Systems</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className='h-8 text-xs'>System</TableHead>
                        <TableHead className='h-8 text-xs'>Vendor</TableHead>
                        <TableHead className='h-8 text-xs'>Purpose</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {submission.data.systems.map((sys) => (
                        <TableRow key={`system-${sys}-${id}`} className='even:bg-muted/50'>
                          <TableCell className='font-medium text-sm py-2'>{sys.name}</TableCell>
                          <TableCell className='text-muted-foreground text-sm py-2'>{sys.access}</TableCell>
                          <TableCell className='text-muted-foreground text-sm py-2'>{sys.purpose}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Forms & Workflows */}
                <div className='pt-4 border-t border-border'>
                  <h4 className='text-sm font-medium text-foreground mb-3'>Forms & Workflows</h4>
                  <div className='grid grid-cols-2 gap-4 text-sm'>
                    <div>
                      <span className='text-muted-foreground text-xs'>Reporting Needed</span>
                      <p className='font-medium'>{submission.data.reportingNeeded}</p>
                    </div>
                    <div>
                      <span className='text-muted-foreground text-xs'>Forms Needed</span>
                      <p className='font-medium'>{submission.data.formsNeeded}</p>
                    </div>
                    {'formsCount' in submission.data && submission.data.formsCount && (
                      <div>
                        <span className='text-muted-foreground text-xs'>Forms Count</span>
                        <p className='font-medium'>{submission.data.formsCount}</p>
                      </div>
                    )}
                    <div>
                      <span className='text-muted-foreground text-xs'>Legacy Data</span>
                      <p className='font-medium'>{submission.data.legacyData}</p>
                    </div>
                  </div>
                </div>

                {/* Department Pain Points */}
                <div className='pt-4 border-t border-border'>
                  <h4 className='text-sm font-medium text-foreground mb-3'>Department Pain Points</h4>
                  <ul className='space-y-1.5 text-sm'>
                    {submission.data.painPoints.map((point, idx) => (
                      <li key={`pain-points-${point}-${id}`} className='flex gap-2'>
                        <span className='text-muted-foreground'>{idx + 1}.</span>
                        <span className='text-gray-600'>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* File Uploads / Attachments */}
                <div className='pt-4 border-t border-border'>
                  <h4 className='text-sm font-medium text-foreground mb-3'>Attachments</h4>
                  {submission.data.files.length === 0 ? (
                    <div className='text-sm text-muted-foreground p-4 bg-muted/30 rounded-md text-center border-2 border-dashed border-border'>
                      No files uploaded
                    </div>
                  ) : (
                    <ul className='space-y-2'>
                      {submission.data.files.map((file, fileIdx) => (
                        <li
                          key={`file-${fileIdx}-${file.name}`}
                          className='flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-border bg-muted/20 text-sm'
                        >
                          <div className='flex items-center gap-2 min-w-0'>
                            <Badge variant='outline' className='shrink-0 text-xs'>
                              {file.label}
                            </Badge>
                            <span className='truncate text-foreground font-medium'>{file.name}</span>
                            <span className='text-muted-foreground shrink-0'>({(file.size / 1024).toFixed(1)} KB)</span>
                          </div>
                          <a
                            href={file.data}
                            download={file.name}
                            className='shrink-0 flex items-center gap-1 text-primary hover:underline text-xs'
                          >
                            <Download className='w-3 h-3' />
                            Download
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Notes / Comments placeholder */}
                <div className='pt-4 border-t border-border'>
                  <h4 className='text-sm font-medium text-foreground mb-3'>Notes</h4>
                  <div className='text-sm text-muted-foreground p-3 bg-muted/30 rounded-md border border-border'>No additional notes</div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* SECTION 3: NEEDS & PRICING */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 pt-8 border-t-2 border-border'>
        {/* LEFT COLUMN — NEEDS */}
        <div className='space-y-4'>
          <div>
            <h2 className='text-xl font-semibold text-foreground mb-1'>Needs</h2>
            <p className='text-sm text-muted-foreground'>System-recommended configuration based on discovery evidence</p>
          </div>

          <Accordion type='multiple' defaultValue={['platform', 'sherpa', 'rover', 'atlas', 'questions']} className='space-y-2'>
            {/* 1) Platform */}
            <AccordionItem value='platform' className='border border-border rounded-md px-4'>
              <AccordionTrigger className='hover:no-underline'>
                <div className='flex items-center gap-2'>
                  <Checkbox
                    checked={platformAccepted}
                    disabled={needsConfirmed}
                    onCheckedChange={(checked) => setPlatformAccepted(checked as boolean)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label='Accept Platform'
                  />
                  <span className='font-medium'>Platform (SEIN Terminal)</span>
                  {platformAccepted && (
                    <Badge variant='outline' className='bg-primary/10 text-primary border-primary/20'>
                      <Check className='w-3 h-3 mr-1' />
                      Accepted
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className='pt-4 space-y-4'>
                <div className='space-y-3 text-sm'>
                  <div>
                    <span className='text-muted-foreground'>Organization Size:</span>
                    <p className='font-medium'>{organizationSizeLabel}</p>
                  </div>
                  <div>
                    <span className='text-muted-foreground'>Environment:</span>
                    <p className='font-medium'>Government / Enterprise</p>
                  </div>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <span className='text-muted-foreground text-xs'>Staff Users</span>
                      <p className='font-medium'>{staffUsers}</p>
                    </div>
                    <div>
                      <span className='text-muted-foreground text-xs'>Departments</span>
                      <p className='font-medium'>{listItems.length.toString()}</p>
                    </div>
                  </div>
                </div>
                {!needsConfirmed && (
                  <div className='flex gap-2 pt-2'>
                    {!platformAccepted && (
                      <Button size='sm' onClick={() => setPlatformAccepted(true)} className='gap-2'>
                        <Check className='w-4 h-4' />
                        Accept
                      </Button>
                    )}
                    {platformAccepted && (
                      <Button size='sm' variant='outline' onClick={() => setPlatformAccepted(false)} className='gap-2'>
                        <Edit2 className='w-4 h-4' />
                        Modify
                      </Button>
                    )}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* 2) Sherpa */}
            <AccordionItem value='sherpa' className='border border-border rounded-md px-4'>
              <AccordionTrigger className='hover:no-underline'>
                <div className='flex items-center gap-2'>
                  <Checkbox
                    checked={sherpaAccepted}
                    disabled={needsConfirmed}
                    onCheckedChange={(checked) => setSherpaAccepted(checked as boolean)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label='Accept Sherpa'
                  />
                  <span className='font-medium'>Sherpa (Project Management)</span>
                  {sherpaAccepted && (
                    <Badge variant='outline' className='bg-primary/10 text-primary border-primary/20'>
                      <Check className='w-3 h-3 mr-1' />
                      Accepted
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className='pt-4 space-y-4'>
                <div className='space-y-3 text-sm'>
                  <div className='w-full'>
                    <Label htmlFor='sherpa-tier' className='text-xs text-muted-foreground'>
                      Recommended Tier
                    </Label>
                    <Select value={sherpaTier} onValueChange={setSherpaTier} disabled={needsConfirmed}>
                      <SelectTrigger id={`sherpa-tier-${id}`} className='w-full mt-1.5'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className='w-full'>
                        <SelectItem value='Starter'>Starter</SelectItem>
                        <SelectItem value='Professional'>Professional</SelectItem>
                        <SelectItem value='Enterprise'>Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <span className='text-muted-foreground'>Project Count:</span>
                    <p className='font-medium'>{projectsFromNumberOfProperties} active projects</p>
                  </div>
                  <div className='space-y-2 pt-2'>
                    <p className='text-xs font-medium text-muted-foreground uppercase'>Modules</p>
                    <div className='space-y-2'>
                      {sherpaPricingData?.features?.map((feature) => {
                        // Map feature names to state keys
                        const featureNameToKey: Record<string, keyof typeof sherpaModules> = {
                          'Budget Tracking': 'budgeting',
                          'Timeline Visualization': 'timeline',
                          'Advanced Reporting': 'reporting',
                        };
                        const featureKey = featureNameToKey[feature.name];
                        const isChecked = featureKey ? (sherpaModules[featureKey] ?? false) : false;
                        const triggerTexts: Record<string, string> = {
                          'Budget Tracking': 'Triggered by: Finance department needs',
                          'Timeline Visualization': 'Triggered by: Cross-department coordination needs',
                          'Advanced Reporting': 'Triggered by: Automated reporting requests',
                        };
                        if (!featureKey) return null;
                        return (
                          <div key={feature.name} className='flex items-start space-x-2'>
                            <Checkbox
                              id={`sherpa-${featureKey}-${id}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => handleSherpaModuleChange(featureKey, checked as boolean)}
                              disabled={needsConfirmed}
                            />
                            <div className='flex-1'>
                              <Label htmlFor={`sherpa-${featureKey}-${id}`} className='text-sm font-normal cursor-pointer'>
                                {feature.name}
                              </Label>
                              <p className='text-xs text-muted-foreground'>{triggerTexts[feature.name] || ''}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {!needsConfirmed && (
                  <div className='flex gap-2 pt-2'>
                    {!sherpaAccepted && (
                      <Button size='sm' onClick={() => setSherpaAccepted(true)} className='gap-2'>
                        <Check className='w-4 h-4' />
                        Accept
                      </Button>
                    )}
                    {sherpaAccepted && (
                      <Button size='sm' variant='outline' onClick={() => setSherpaAccepted(false)} className='gap-2'>
                        <Edit2 className='w-4 h-4' />
                        Modify
                      </Button>
                    )}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* 3) Rover */}
            <AccordionItem value='rover' className='border border-border rounded-md px-4'>
              <AccordionTrigger className='hover:no-underline'>
                <div className='flex items-center gap-2'>
                  <Checkbox
                    checked={roverAccepted}
                    disabled={needsConfirmed}
                    onCheckedChange={(checked) => setRoverAccepted(checked as boolean)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label='Accept Rover'
                  />
                  <span className='font-medium'>Rover (Public Forms)</span>
                  {roverAccepted && (
                    <Badge variant='outline' className='bg-primary/10 text-primary border-primary/20'>
                      <Check className='w-3 h-3 mr-1' />
                      Accepted
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className='pt-4 space-y-4'>
                <div className='space-y-3 text-sm'>
                  <div className='w-full'>
                    <Label htmlFor={`rover-tier-${id}`} className='text-xs text-muted-foreground'>
                      Recommended Tier
                    </Label>
                    <Select value={roverTier} onValueChange={setRoverTier} disabled={needsConfirmed}>
                      <SelectTrigger id={`rover-tier-${id}`} className='w-full mt-1.5'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='Starter'>Starter</SelectItem>
                        <SelectItem value='Standard'>Standard</SelectItem>
                        <SelectItem value='Professional'>Professional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <span className='text-muted-foreground'>Public-Facing Forms:</span>
                    <p className='font-medium'>{publicForms} forms identified</p>
                  </div>
                  <div className='space-y-2 pt-2'>
                    <p className='text-xs font-medium text-muted-foreground uppercase'>Modules</p>
                    <div className='space-y-2'>
                      {roverPricingData?.features?.map((feature) => {
                        const featureNameToKey: Record<string, keyof typeof roverModules> = {
                          'Workflow Engine': 'workflow',
                          'Email Notifications': 'notifications',
                          'Form Analytics': 'analytics',
                        };
                        const featureKey = featureNameToKey[feature.name];
                        const isChecked = featureKey ? (roverModules[featureKey] ?? false) : false;
                        const triggerTexts: Record<string, string> = {
                          'Workflow Engine': 'Triggered by: Multi-step approval processes',
                          'Email Notifications': 'Triggered by: Public-facing submission needs',
                          'Form Analytics': 'Triggered by: Reporting and visibility needs',
                        };
                        if (!featureKey) return null;
                        return (
                          <div key={feature.name} className='flex items-start space-x-2'>
                            <Checkbox
                              id={`rover-${featureKey}-${id}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => handleRoverModuleChange(featureKey, checked as boolean)}
                              disabled={needsConfirmed}
                            />
                            <div className='flex-1'>
                              <Label htmlFor={`rover-${featureKey}-${id}`} className='text-sm font-normal cursor-pointer'>
                                {feature.name}
                              </Label>
                              <p className='text-xs text-muted-foreground'>{triggerTexts[feature.name] || ''}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {!needsConfirmed && (
                  <div className='flex gap-2 pt-2'>
                    {!roverAccepted && (
                      <Button size='sm' onClick={() => setRoverAccepted(true)} className='gap-2'>
                        <Check className='w-4 h-4' />
                        Accept
                      </Button>
                    )}
                    {roverAccepted && (
                      <Button size='sm' variant='outline' onClick={() => setRoverAccepted(false)} className='gap-2'>
                        <Edit2 className='w-4 h-4' />
                        Modify
                      </Button>
                    )}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* 4) Atlas */}
            <AccordionItem value='atlas' className='border border-border rounded-md px-4'>
              <AccordionTrigger className='hover:no-underline'>
                <div className='flex items-center gap-2'>
                  <Checkbox
                    checked={atlasAccepted}
                    disabled={needsConfirmed}
                    onCheckedChange={(checked) => setAtlasAccepted(checked as boolean)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label='Accept Atlas'
                  />
                  <span className='font-medium'>Atlas (Property Management)</span>
                  {atlasAccepted && (
                    <Badge variant='outline' className='bg-primary/10 text-primary border-primary/20'>
                      <Check className='w-3 h-3 mr-1' />
                      Accepted
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className='pt-4 space-y-4'>
                <div className='space-y-3 text-sm'>
                  <div className='w-full'>
                    <Label htmlFor={`atlas-tier-${id}`} className='text-xs text-muted-foreground'>
                      Recommended Tier
                    </Label>
                    <Select value={atlasTier} onValueChange={setAtlasTier} disabled={needsConfirmed}>
                      <SelectTrigger id={`atlas-tier-${id}`} className='w-full mt-1.5'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='Starter'>Starter</SelectItem>
                        <SelectItem value='Standard'>Standard</SelectItem>
                        <SelectItem value='Professional'>Professional</SelectItem>
                        <SelectItem value='Enterprise'>Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <span className='text-muted-foreground'>Inventory Count:</span>
                    <p className='font-medium'>~2,500 properties (estimated)</p>
                  </div>
                  <div className='space-y-2 pt-2'>
                    <p className='text-xs font-medium text-muted-foreground uppercase'>Modules</p>
                    <div className='space-y-2'>
                      {atlasPricingData?.features?.map((feature) => {
                        const featureNameToKey: Record<string, keyof typeof atlasModules> = {
                          'GIS Integration': 'mapping',
                          'Inspection Workflow': 'inspection',
                        };
                        const featureKey = featureNameToKey[feature.name];
                        const isChecked = featureKey ? (atlasModules[featureKey] ?? false) : false;
                        const triggerTexts: Record<string, string> = {
                          'GIS Integration': 'Triggered by: Existing GIS system usage',
                          'Inspection Workflow': 'Triggered by: Building & Code inspection tracking',
                        };
                        if (!featureKey) return null;
                        return (
                          <div key={feature.name} className='flex items-start space-x-2'>
                            <Checkbox
                              id={`atlas-${featureKey}-${id}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => handleAtlasModuleChange(featureKey, checked as boolean)}
                              disabled={needsConfirmed}
                            />
                            <div className='flex-1'>
                              <Label htmlFor={`atlas-${featureKey}-${id}`} className='text-sm font-normal cursor-pointer'>
                                {feature.name}
                              </Label>
                              <p className='text-xs text-muted-foreground'>{triggerTexts[feature.name] || ''}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {!needsConfirmed && (
                  <div className='flex gap-2 pt-2'>
                    {!atlasAccepted && (
                      <Button size='sm' onClick={() => setAtlasAccepted(true)} className='gap-2'>
                        <Check className='w-4 h-4' />
                        Accept
                      </Button>
                    )}
                    {atlasAccepted && (
                      <Button size='sm' variant='outline' onClick={() => setAtlasAccepted(false)} className='gap-2'>
                        <Edit2 className='w-4 h-4' />
                        Modify
                      </Button>
                    )}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* 5) Open Questions / Risks */}
            <AccordionItem value='questions' className='border border-border rounded-md px-4'>
              <AccordionTrigger className='hover:no-underline'>
                <div className='flex items-center gap-2'>
                  <Checkbox
                    checked={questionsReviewed}
                    disabled={needsConfirmed}
                    onCheckedChange={(checked) => setQuestionsReviewed(checked as boolean)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label='Mark questions reviewed'
                  />
                  <span className='font-medium'>Open Questions / Risks</span>
                  {questionsReviewed && (
                    <Badge variant='outline' className='bg-primary/10 text-primary border-primary/20'>
                      <Check className='w-3 h-3 mr-1' />
                      Reviewed
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className='pt-4'>
                <ul className='space-y-2 text-sm'>
                  <li className='flex gap-2'>
                    <span className='text-muted-foreground'>•</span>
                    <span>GIS system integration complexity needs technical scoping</span>
                  </li>
                  <li className='flex gap-2'>
                    <span className='text-muted-foreground'>•</span>
                    <span>Legacy data migration timeline not yet confirmed</span>
                  </li>
                  <li className='flex gap-2'>
                    <span className='text-muted-foreground'>•</span>
                    <span>IT department capacity for API integration unknown</span>
                  </li>
                  <li className='flex gap-2'>
                    <span className='text-muted-foreground'>•</span>
                    <span>Annual budget authority confirmation pending</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Confirm Needs Button */}
          {!needsConfirmed && (
            <div className='pt-4'>
              <Button
                onClick={handleConfirmNeeds}
                disabled={!platformAccepted || !sherpaAccepted || !roverAccepted || !atlasAccepted || !questionsReviewed}
                className='w-full gap-2'
              >
                <Check className='w-4 h-4' />
                Confirm All Needs
              </Button>
              {(!platformAccepted || !sherpaAccepted || !roverAccepted || !atlasAccepted || !questionsReviewed) && (
                <p className='text-xs text-muted-foreground text-center mt-2'>
                  All products and open questions must be checked before confirming needs
                </p>
              )}
            </div>
          )}

          {needsConfirmed && (
            <div className='pt-4 p-4 bg-primary/10 border border-primary/20 rounded-md'>
              <div className='flex items-center gap-2 text-primary'>
                <Check className='w-4 h-4' />
                <span className='font-medium text-sm'>Needs Confirmed</span>
              </div>
              <p className='text-xs text-muted-foreground mt-1'>Configuration locked. Pricing is now ready for review.</p>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN — PRICING */}
        <div className='space-y-4'>
          <div>
            <h2 className='text-xl font-semibold text-foreground mb-1'>Pricing</h2>
            <p className='text-sm text-muted-foreground'>Derived from confirmed needs</p>
          </div>

          <Card className={needsConfirmed ? '' : 'opacity-50 pointer-events-none'}>
            <CardHeader>
              <CardTitle className='text-base'>Annual Investment</CardTitle>
            </CardHeader>
            <CardContent className='space-y-6'>
              {/* Budget Percentage KPI */}
              <div className='p-4 bg-primary/10 border border-primary/20 rounded-md'>
                <div className='text-sm text-muted-foreground mb-1'>SEIN Terminal as % of Annual Budget</div>
                <div className='flex items-baseline gap-2'>
                  <div className='text-3xl font-semibold text-primary'>{budgetPercentage}%</div>
                  <div className='text-sm text-muted-foreground'>
                    ${annualTotal.toLocaleString()} of ${fileData.annualBudget.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Platform Fee */}
              <div className='space-y-2'>
                <div className='flex items-center justify-between text-sm'>
                  <span className='font-medium'>Platform (SEIN Terminal)</span>
                  <span className='font-semibold'>${platformFee.toLocaleString()}</span>
                </div>
              </div>

              {/* Sherpa */}
              <div className='space-y-2 pt-4 border-t border-border'>
                <div className='flex items-center justify-between text-sm'>
                  <span className='font-medium'>Sherpa (Project Management)</span>
                  <span className='font-semibold'>${sherpaBaseFee.toLocaleString()}</span>
                </div>
                {Object.entries(sherpaModules).filter(([_, enabled]) => enabled).length > 0 && (
                  <div className='ml-4 space-y-1'>
                    {sherpaPricingData?.features
                      ?.filter((feature) => {
                        const featureNameToKey: Record<string, keyof typeof sherpaModules> = {
                          'Budget Tracking': 'budgeting',
                          'Timeline Visualization': 'timeline',
                          'Advanced Reporting': 'reporting',
                        };
                        const featureKey = featureNameToKey[feature.name];
                        return featureKey && sherpaModules[featureKey];
                      })
                      .map((feature) => (
                        <div key={feature.name} className='flex items-center justify-between text-xs text-muted-foreground'>
                          <span>• {feature.name}</span>
                          <span>${feature.cost.toLocaleString()}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Rover */}
              <div className='space-y-2 pt-4 border-t border-border'>
                <div className='flex items-center justify-between text-sm'>
                  <span className='font-medium'>Rover (Public Forms)</span>
                  <span className='font-semibold'>${roverBaseFee.toLocaleString()}</span>
                </div>
                {Object.entries(roverModules).filter(([_, enabled]) => enabled).length > 0 && (
                  <div className='ml-4 space-y-1'>
                    {roverPricingData?.features
                      ?.filter((feature) => {
                        const featureNameToKey: Record<string, keyof typeof roverModules> = {
                          'Workflow Engine': 'workflow',
                          'Email Notifications': 'notifications',
                          'Form Analytics': 'analytics',
                        };
                        const featureKey = featureNameToKey[feature.name];
                        return featureKey && roverModules[featureKey];
                      })
                      .map((feature) => (
                        <div key={feature.name} className='flex items-center justify-between text-xs text-muted-foreground'>
                          <span>• {feature.name}</span>
                          <span>${feature.cost.toLocaleString()}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Atlas */}
              <div className='space-y-2 pt-4 border-t border-border'>
                <div className='flex items-center justify-between text-sm'>
                  <span className='font-medium'>Atlas (Property Management)</span>
                  <span className='font-semibold'>${atlasBaseFee.toLocaleString()}</span>
                </div>
                {Object.entries(atlasModules).filter(([_, enabled]) => enabled).length > 0 && (
                  <div className='ml-4 space-y-1'>
                    {atlasPricingData?.features
                      ?.filter((feature) => {
                        const featureNameToKey: Record<string, keyof typeof atlasModules> = {
                          'GIS Integration': 'mapping',
                          'Inspection Workflow': 'inspection',
                        };
                        const featureKey = featureNameToKey[feature.name];
                        return featureKey && atlasModules[featureKey];
                      })
                      .map((feature) => (
                        <div key={feature.name} className='flex items-center justify-between text-xs text-muted-foreground'>
                          <span>• {feature.name}</span>
                          <span>${feature.cost.toLocaleString()}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Annual Total */}
              <div className='pt-4 border-t-2 border-border'>
                <div className='flex items-center justify-between'>
                  <span className='font-semibold'>Annual Total</span>
                  <span className='text-xl font-bold text-primary'>${annualTotal.toLocaleString()}</span>
                </div>
              </div>

              {/* Approve Pricing Button */}
              {!pricingApproved && needsConfirmed && (
                <div className='pt-4'>
                  <Button
                    onClick={handleApprovePricing}
                    variant='default'
                    className='w-full gap-2'
                    disabled={savePricingMutation.isLoading}
                  >
                    <Check className='w-4 h-4' />
                    {savePricingMutation.isLoading ? 'Saving Pricing...' : 'Approve Pricing'}
                  </Button>
                </div>
              )}

              {pricingApproved && (
                <div className='pt-4 p-4 bg-primary border border-primary rounded-md'>
                  <div className='flex items-center gap-2 text-primary-foreground'>
                    <Check className='w-5 h-5' />
                    <span className='font-semibold'>Pricing Approved</span>
                  </div>
                  <p className='text-xs text-primary-foreground/80 mt-1'>Configuration and pricing locked for procurement</p>
                </div>
              )}
            </CardContent>
          </Card>

          {!needsConfirmed && (
            <div className='p-4 bg-muted/50 border border-border rounded-md'>
              <p className='text-sm text-muted-foreground text-center'>Pricing will be available once all needs are confirmed</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
